import React, { useState, useEffect, useContext } from "react";
import { Table, Layout, message, Alert } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../Constants";
import AppContext from "../../AppContext";
import { useTranslation } from "react-i18next";

function CompanyListPage() {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subCompanyUsage, setSubCompanyUsage] = useState(null);
  const navigate = useNavigate();
  const { profile } = useContext(AppContext);

  const isSubCompanyAdmin =
    profile.role !== "super_admin" && !!profile.parentCompanyId;
  const canCreateCompany = profile.role === "super_admin" || !isSubCompanyAdmin;

  // Parent admins have a MaxSubCompanies limit; super admins create top-level parents (no limit).
  const atSubCompanyLimit =
    profile.role !== "super_admin" &&
    subCompanyUsage &&
    subCompanyUsage.max != null &&
    subCompanyUsage.max > 0 &&
    subCompanyUsage.used >= subCompanyUsage.max;

  useEffect(() => {
    fetchCompanies();
    if (profile.role !== "super_admin" && profile.companyId) {
      axios
        .get(`${BASE_URL}/Company/sub-company-usage?companyId=${profile.companyId}`)
        .then((res) => setSubCompanyUsage(res.data))
        .catch((err) =>
          console.error("Error fetching sub-company usage:", err)
        );
    }
  }, []);

  useEffect(() => {
    const lowercasedFilter = searchText.toLowerCase();
    const filtered = companies.filter((company) => {
      return company.name.toLowerCase().includes(lowercasedFilter);
    });
    setFilteredData(filtered);
  }, [searchText, companies]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(
        profile.role === "super_admin"
          ? `${BASE_URL}/Company/GetParentCompanies`
          : `${BASE_URL}/Company?companyId=${profile.companyId}`
      );
      setCompanies(response.data);
      setFilteredData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error(t("messages.error.fetchCompany"));
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    setFilteredData(companies);
  };

  const columns = [
    {
      title: t("form.name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: "200px",
    },
    {
      title: t("form.description"),
      dataIndex: "description",
      key: "description",
      width: "300px",
    },
    {
      title: t("form.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("form.phone"),
      dataIndex: "phone",
      key: "phone",
    },
    ...(profile.role !== "super_admin" ? [{
      title: t("form.companyType"),
      key: "companyType",
      render: (_, record) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          record.parentCompanyId === null 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {record.parentCompanyId === null ? t("placeholders.company.parent") : t("placeholders.company.sub")}
        </span>
      ),
      width: "220px",
    }] : []),
    {
      title: t("table.admin.actions"),
      key: "action",
      render: (_, record) => (
        <div className="space-x-2">
          <button
            type="button"
            onClick={() => navigate(`/company-form/${record.id}`)}
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-2 rounded flex items-center"
          >
            <EditOutlined />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white">
            {t("navigation.company")}
          </h1>
        </div>
      </header>
      <div className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
        <div className="">
          {atSubCompanyLimit && (
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message={t(
                "company.subLimitReached",
                "Sub-company limit reached"
              )}
              description={`You have reached your sub-company limit (${subCompanyUsage.used}/${subCompanyUsage.max}). Contact your administrator to increase the limit before adding another sub-company.`}
            />
          )}
          <div className="flex justify-between gap-2 items-center mb-6">
            <div className="relative w-3/4">
              <SearchOutlined className="absolute w-full top-2 left-2 text-gray-400" />
              <input
                type="text"
                placeholder={t("placeholders.company.name")}
                value={searchText}
                style={{ paddingLeft: "30px" }}
                onChange={(e) => setSearchText(e.target.value)}
                className="border border-gray-300 w-full p-2 rounded"
              />
              {searchText && (
                <CloseCircleOutlined
                  className="absolute top-2 right-2 text-gray-400 cursor-pointer"
                  onClick={handleClearSearch}
                />
              )}
            </div>
            <div className="space-x-2">
              {/* Sub-Company admins cannot create companies (one-level hierarchy).
                  Parent admins are blocked once MaxSubCompanies is reached.
                  Backend enforces both regardless of the button. */}
              {canCreateCompany && (
                <button
                  type="button"
                  onClick={() =>
                    atSubCompanyLimit
                      ? message.error(
                          t(
                            "company.subLimitReached",
                            "Sub-company limit reached"
                          )
                        )
                      : navigate("/company-form")
                  }
                  disabled={atSubCompanyLimit}
                  className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-lg flex items-center"
                >
                  <PlusOutlined className="mr-2" />
                  {t("buttons.new")}{" "}
                  <span className="lg:inline-block hidden ml-1">
                    {t("navigation.company")}
                  </span>
                </button>
              )}
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            className="[&_.ant-table-thead_.ant-table-cell]:bg-gray-100 [&_.ant-table-thead_.ant-table-cell]:text-black"
            rowClassName={(record, index) =>
              `${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              } hover:bg-gray-100 transition-colors`
            }
            loading={loading}
            scroll={{ x: true }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
          />
        </div>
      </div>
    </Layout>
  );
}

export default CompanyListPage;
